/**
 * The coefficient of variation: the standard deviation as a fraction of the mean.
 *
 * Only meaningful on a ratio scale — positive values with a true zero (lengths, masses,
 * reaction times, incomes). On an interval scale like °C the zero is arbitrary, so the
 * same temperatures give a different CV in °F; near a zero mean the ratio explodes.
 */

import { mean, standardDeviation } from "@/lib/stats/descriptive";

export interface VariationReport {
  n: number;
  mean: number;
  sd: number;
  /** sd / mean as a proportion (0.12 = 12 %); NaN when the mean is zero. */
  cv: number;
  population: boolean;
  hasNegative: boolean;
}

export function variationReport(
  values: readonly number[],
  population: boolean
): VariationReport | null {
  const n = values.length;
  if (n < (population ? 1 : 2)) {
    return null;
  }
  const m = mean(values);
  const sd = standardDeviation(values, population);
  return {
    n,
    mean: m,
    sd,
    cv: m === 0 ? Number.NaN : sd / m,
    population,
    hasNegative: values.some((value) => value < 0),
  };
}
